import {useEffect, useMemo, useRef, useState} from 'react';
import {ApiError, errorMessageKey, newIdempotencyKey} from '../../api';
import type {CreatePublicBookingRequest} from '../../api/types';
import {useRouter} from '../../app/router';
import {href, QR_PARAMS, ROUTES} from '../../app/routes';
import {
  composeBookingTime,
  dateLabel,
  defaultTimeSlots,
  saveBooking,
  upcomingDates,
  weekdayShort,
} from '../../booking';
import {useI18n} from '../../i18n';
import {useApi} from '../../session';
import {
  Button,
  Card,
  QtyStepper,
  Screen,
  ScreenBody,
  ScreenFooter,
  SplitRow,
  Stack,
  TopBar,
} from '../../ui';
import {LanguageToggle} from '../Q02LanguageToggle';

/**
 * # BookWeb · the public booking form
 *
 * Route `/book?m=<tenant_id>` — reached from a **booking link** the venue
 * publishes, not a scanned table QR. So it opens no guest session and carries
 * only the tenant, read from the `m` query parameter exactly as the QR entries
 * read theirs (`routes.ts`). No `m` means the link was typed, shared truncated,
 * or is not a booking link at all — that is Q19's dead end, the same answer the
 * QR flow gives a tokenless landing.
 *
 * ## What it collects, and what it does not
 *
 * Name, phone, party size, date and time — the create body. **No zone picker.**
 * The design package draws zone chips (`BookWeb.dc.html`), but no guest-credential
 * endpoint in the contract lists a venue's zones, and offering a picker over
 * zones the client cannot name is the `table_id`-as-label mistake again. `zone_id`
 * stays in the request type for the day a zones read for guests lands; until then
 * the form omits it and the server takes the booking without one.
 *
 * ## The time is wall time, composed on the device but not projected through it
 *
 * The slots and the date list are wall-clock values (`src/booking/datetime.ts`).
 * The booking flow has no venue timezone — no service-status read has run — and a
 * booking is a wall-clock intent regardless: "19:00 at the venue" is 19:00 to a
 * tourist on another continent. So `booking_time` is a naive `YYYY-MM-DDTHH:mm:00`
 * and the server resolves it in the venue's zone. Slot *availability* and real
 * trading hours are likewise unknown to a guest, so every slot is offered and the
 * server is the authority — an out-of-hours or full time comes back a validation
 * error, surfaced here, rather than greyed out from knowledge the client lacks.
 *
 * ## REDESIGN · the existing web look, not the bible restyle
 *
 * Built from `src/ui` — the same `Screen`/`TopBar`/`Card`/`Button`/`QtyStepper`
 * the thirteen Qxx screens use — so it sits inside the current surface. The bible
 * restyle is a separate, coupled effort and is deliberately not attempted here.
 */
export default function BookWeb() {
  const {t, language} = useI18n();
  const {navigate, query} = useRouter();
  const client = useApi();

  const tenantId = (query.get(QR_PARAMS.tenant) ?? '').trim();

  const dates = useMemo(() => upcomingDates(14), []);
  const slots = useMemo(() => defaultTimeSlots(), []);

  const [dateIso, setDateIso] = useState<string>(() => dates[0]?.iso ?? '');
  const [time, setTime] = useState<string | null>(null);
  const [partySize, setPartySize] = useState(2);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  // A tokenless / tenantless link is not a booking link. Q19 owns that.
  useEffect(() => {
    if (!tenantId) navigate(ROUTES.invalid, {replace: true});
  }, [tenantId, navigate]);

  // One idempotency key per submit *intent*: reused on a retry of the same form
  // so a double-tap or a post-timeout retry cannot hold two tables, and cleared
  // whenever the form changes so an edited retry is a new intent (the server
  // rejects the same key with a different body).
  const idemRef = useRef<string | null>(null);
  useEffect(() => {
    idemRef.current = null;
  }, [name, phone, partySize, dateIso, time]);

  const trimmedName = name.trim();
  const trimmedPhone = phone.trim();
  const missingReasonKey = !trimmedName
    ? ('book.needName' as const)
    : !trimmedPhone
      ? ('book.needPhone' as const)
      : !time
        ? ('book.needTime' as const)
        : null;

  async function submit() {
    if (missingReasonKey || !time || submitting || !tenantId) return;
    setSubmitting(true);
    setError(null);
    const key = idemRef.current ?? (idemRef.current = newIdempotencyKey());
    const bookingTime = composeBookingTime(dateIso, time);
    const request: CreatePublicBookingRequest = {
      tenant_id: tenantId,
      name: trimmedName,
      phone: trimmedPhone,
      party_size: partySize,
      booking_time: bookingTime,
    };
    try {
      const {booking, manage_token} = await client.createPublicBooking(request, key);
      saveBooking({
        tenantId,
        bookingId: booking.booking_id,
        manageToken: manage_token,
        name: booking.name || trimmedName,
        phone: booking.phone || trimmedPhone,
        partySize: booking.party_size || partySize,
        bookingTime: booking.booking_time || bookingTime,
        reference: booking.reference ?? null,
      });
      // `replace`: the form is spent, and a back gesture must not resubmit it.
      navigate(href(ROUTES.bookDone, {token: manage_token}, {[QR_PARAMS.tenant]: tenantId}), {
        replace: true,
      });
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError({kind: 'parse', message: String(caught)}),
      );
      setSubmitting(false);
    }
  }

  const summaryDate = dateIso ? dateLabel(dateIso, language) : '';
  const summary = t('book.summary', {
    date: summaryDate,
    time: time ?? '—',
    count: partySize,
  });

  return (
    <Screen className="bookweb">
      <style href="bookweb" precedence="medium">
        {CSS}
      </style>

      <TopBar trailing={<LanguageToggle />} />

      <ScreenBody className="bookweb-body">
        <div>
          <h1 className="pk-h1">{t('book.title')}</h1>
          <p className="pk-muted">{t('book.lead')}</p>
        </div>

        {/* Date — a horizontal rail of tall day buttons. */}
        <section className="bookweb-field">
          <p className="pk-strong bookweb-label">{t('book.dateLabel')}</p>
          <div className="bookweb-dates" role="group" aria-label={t('book.dateLabel')}>
            {dates.map((d) => {
              const selected = d.iso === dateIso;
              return (
                <button
                  key={d.iso}
                  type="button"
                  className="bookweb-date pokpok-tap"
                  aria-pressed={selected}
                  aria-label={t('book.pickDate', {date: dateLabel(d.iso, language)})}
                  onClick={() => setDateIso(d.iso)}
                >
                  <span className="bookweb-date-dow">
                    {d.isToday ? t('book.today') : weekdayShort(d.iso, language)}
                  </span>
                  <span className="bookweb-date-day">{d.day}</span>
                </button>
              );
            })}
          </div>
        </section>

        {/* Time — a four-column grid. */}
        <section className="bookweb-field">
          <p className="pk-strong bookweb-label">{t('book.timeLabel')}</p>
          <div className="bookweb-times" role="group" aria-label={t('book.timeLabel')}>
            {slots.map((slot) => {
              const selected = slot === time;
              return (
                <button
                  key={slot}
                  type="button"
                  className="bookweb-time pokpok-tap"
                  aria-pressed={selected}
                  aria-label={t('book.pickTime', {time: slot})}
                  onClick={() => setTime(slot)}
                >
                  {slot}
                </button>
              );
            })}
          </div>
        </section>

        {/* Party size — the shared stepper, in a card row. */}
        <Card>
          <SplitRow>
            <span className="pk-strong">{t('book.partyLabel')}</span>
            <QtyStepper
              value={partySize}
              onChange={setPartySize}
              min={1}
              max={30}
              decreaseLabel={t('book.partyDecrease')}
              increaseLabel={t('book.partyIncrease')}
              valueLabel={t('book.partyValueLabel', {count: partySize})}
            />
          </SplitRow>
        </Card>

        {/* Contact. */}
        <Card>
          <Stack>
            <label className="bookweb-input-label">
              <span className="pk-strong">{t('book.nameLabel')}</span>
              <input
                className="pk-input"
                type="text"
                value={name}
                autoComplete="name"
                placeholder={t('book.namePlaceholder')}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="bookweb-input-label">
              <span className="pk-strong">{t('book.phoneLabel')}</span>
              <input
                className="pk-input"
                type="tel"
                inputMode="tel"
                value={phone}
                autoComplete="tel"
                placeholder={t('book.phonePlaceholder')}
                onChange={(e) => setPhone(e.target.value)}
              />
            </label>
          </Stack>
        </Card>

        <p className="pk-hint bookweb-centred">{t('book.holdNote')}</p>

        {error ? (
          <Card tone="warning" aria-live="polite">
            <Stack>
              <p>{t(errorMessageKey(error))}</p>
              <Button variant="secondary" onClick={() => void submit()}>
                {t('common.retry')}
              </Button>
            </Stack>
          </Card>
        ) : null}
      </ScreenBody>

      <ScreenFooter>
        <p className="pk-muted bookweb-centred">{summary}</p>
        <Button
          variant="primary"
          type="button"
          block
          disabled={missingReasonKey !== null || submitting}
          disabledReason={missingReasonKey ? t(missingReasonKey) : undefined}
          reasonVisible={false}
          onClick={() => void submit()}
        >
          {submitting ? t('book.submitting') : t('book.submit')}
        </Button>
        <p className="pk-hint bookweb-centred">{t('common.attribution')}</p>
      </ScreenFooter>
    </Screen>
  );
}

const CSS = `
.bookweb {
  max-width: 34rem;
  margin: 0 auto;
  width: 100%;
}

.bookweb-body {
  display: flex;
  flex-direction: column;
  gap: var(--pokpok-space-4);
}

.bookweb-field {
  display: flex;
  flex-direction: column;
  gap: var(--pokpok-space-2);
}

.bookweb-label {
  margin: 0;
}

/* A horizontal rail; each day is a fixed tall button. */
.bookweb-dates {
  display: flex;
  gap: var(--pokpok-space-2);
  overflow-x: auto;
  padding-bottom: var(--pokpok-space-1);
  -webkit-overflow-scrolling: touch;
}

.bookweb-date {
  flex: 0 0 auto;
  width: 64px;
  min-height: 72px;
  border-radius: var(--pokpok-radius-2);
  border: 1px solid var(--pokpok-color-border-warm);
  background: var(--color-background-card);
  color: var(--color-text-primary);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
}

.bookweb-date[aria-pressed='true'] {
  border: 2px solid var(--color-accent);
  color: var(--color-text-accent);
}

.bookweb-date-dow {
  font-size: 12px;
  color: var(--color-text-secondary);
}

.bookweb-date[aria-pressed='true'] .bookweb-date-dow {
  color: var(--color-text-accent);
}

.bookweb-date-day {
  font-size: 20px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.bookweb-times {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--pokpok-space-2);
}

.bookweb-time {
  min-height: 48px;
  border-radius: var(--pokpok-radius-2);
  border: 1px solid var(--pokpok-color-border-warm);
  background: var(--color-background-card);
  color: var(--color-text-primary);
  font-size: 16px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.bookweb-time[aria-pressed='true'] {
  border: 2px solid var(--color-accent);
  color: var(--color-text-accent);
}

.bookweb-input-label {
  display: flex;
  flex-direction: column;
  gap: var(--pokpok-space-2);
}

.bookweb-centred {
  text-align: center;
}
`;
